import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ProgressBar from "components/ProgressBar";
import Button from "components/Button";
import Dropdown from "components/Dropdown";
import ModalGeneralSuccessfully from "screens/ModalGeneralSuccessfully";
import ModalGeneralFailed from "screens/ModalGeneralFailed";
//import ModalPartialAuthorizationAccepted from "screens/ModalPartialAuthorizationAccepted";
import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import {
  GET_ALL_AVAILABLE_DEVICES,
  MAKE_PAYMENT_BY_GROUP_ID,
  GET_PAYMENT_TERMINAL_REQUEST_BY_ID
} from "graphql/service";
import { Head, Container, Content, Footer, StateContainer } from "./styles";
import { PaymentTerminalIcon } from "assets/icons";
import { debouncedErrorMessage } from "utils/messageUtil";

const Payment = ({status, setStatus}) => {

  const history = useHistory();
  const dispatch = useDispatch();
  const groupId = useParams().groupId;
  const { socketId, connected, error } = useSelector((state) => state.socketReducer);

  const [method, setMethod] = useState("");
  const [makePaymentId, setMakePaymentId] = useState(null);
  const [options, setOptions] = useState([
    { label: "Manual Entry", value: "Manual Entry" },
  ]);

  const [makePaymentLoading, setMakePaymentLoading] = useState(false);
  const [paymentSuccessfullyIsOpen, setPaymentSuccessfullyIsOpen] = useState(false);
  const [paymentFailedIsOpen, setPaymentFailedIsOpen] = useState(false);
  // const [partialAuthorizationIsOpen, setPartialAuthorizationIsOpen] = useState(false);
  // const [credit, setCredit] = useState(0);
  // const [balance, setBalance] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!connected) return; 

    const handlePaymentComplete = (data) => {
      if(data?.status === "SUCCESS") {
        setPaymentFailedIsOpen(false);
        setPaymentSuccessfullyIsOpen(true);
      } else {
        setPaymentFailedIsOpen(true);
        setErrorMessage(data?.message);
      }
      stopPolling();
    };

    const handlePaymentFailed = (data) => {
      setPaymentFailedIsOpen(true);
      setErrorMessage(data?.message);
      stopPolling();
    };

    dispatch({
      type: 'socket/on',
      payload: { event: 'PAYMENT_TRANSACTION_COMPLETE', callback: handlePaymentComplete }
    });

    dispatch({
      type: 'socket/on',
      payload: { event: 'PAYMENT_TRANSACTION_FAILED', callback: handlePaymentFailed }
    });

    return () => {
      dispatch({
        type: 'socket/off',
        payload: { event: 'PAYMENT_TRANSACTION_COMPLETE' }
      });

      dispatch({
        type: 'socket/off',
        payload: { event: 'PAYMENT_TRANSACTION_FAILED' }
      });
    };
  }, [dispatch, connected]);

  useQuery(GET_ALL_AVAILABLE_DEVICES, {
    onCompleted: (devicesData) => {
      if (devicesData.devices.length > 0) {
        const newOptions = devicesData.devices?.map(
          (device) => ({ label: device.name, value: device.deviceId })
        );
        newOptions.push({label: "Manual Entry", value: "Manual Entry"})
        setOptions(newOptions);
      }
    },
    onError: (error) => {
      if (!error.networkError) {
        let content = error.message;
        if (error.message.includes("Validation")) {
          content = JSON.stringify(
            error.graphQLErrors[0]?.extensions?.exception
          );
        }
        debouncedErrorMessage(content);
      }
    },
  });

  const [makePayment, { loading }] = useMutation(MAKE_PAYMENT_BY_GROUP_ID, {
    onCompleted: (data) => {
      setMakePaymentId(data.makePayment?.makePaymentId);
      setMakePaymentLoading(true);
    },
    onError: (error) => {
      setMakePaymentLoading(false);
      setPaymentFailedIsOpen(true);
      setErrorMessage(error.message);
    },
  });

  const [getPaymentTerminalRequestByID, { startPolling, stopPolling }] = useLazyQuery(GET_PAYMENT_TERMINAL_REQUEST_BY_ID, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const status = data?.getPaymentTerminalRequestByID?.status;

      if (status === "SUCCESS") {
        setPaymentFailedIsOpen(false);
        setPaymentSuccessfullyIsOpen(true);
        stopPolling(); 
      } else if (status === "PENDING") {
        setPaymentFailedIsOpen(false);
      } else {
        stopPolling(); 
      }
    },
    onError: (error) => {
      setPaymentFailedIsOpen(true);
      setErrorMessage(error.message);
      stopPolling();
    },
  });


  useEffect(() => {
    if (!makePaymentId) return;

    getPaymentTerminalRequestByID({ variables: { id: makePaymentId } });
    startPolling(10000);
    return () => {
      stopPolling();
    };
  }, [makePaymentId, getPaymentTerminalRequestByID, startPolling, stopPolling]);

  const handleSend = () => {
    if (makePaymentLoading || loading) return;
    if (method === "") {
      const content = "Please select a method";
      debouncedErrorMessage(content);
    } else if (method === "Manual Entry") {
      history.push(`/group-check-in/credit-card/${groupId}`);
    } else {
      //setPartialAuthorizationIsOpen(false);
      setPaymentFailedIsOpen(false);
      setPaymentSuccessfullyIsOpen(false);
      makePayment({
        variables: {
          input: {
            groupId: groupId,
            deviceId: method,
            pmsSocketId: socketId,
          },
        },
      });
    }
  };

  return (
    <Container>
      <ProgressBar step={3} groupId={groupId} status={status}/>
      {makePaymentLoading ? (
        <StateContainer>
          <div className="instruction">
            <div className="icon">
              <PaymentTerminalIcon />
            </div>
            <div className="title">
              <div>
                {`Follow the instructions on the payment terminal at ${options.find((option) => option.value === method)?.label}.`}
              </div>
            </div>
          </div>
        </StateContainer>
      ) : (
        <>
          <Content>
            <Head>Payment Device</Head>
            <div className="text">
              <div>Which payment terminal would you like to use?</div>
            </div>
            <Dropdown
              mainPage
              options={options}
              value={
                options.find((option) => option.value === method)?.label ||
                "- Select -"
              }
              title="PREFERRED DEVICE"
              onChange={(e) => {
                setMethod(e);
              }}
            />
          </Content>
          <Footer>
            <div className="buttonContainer">
              <Button
                mainPage
                success
                onClick={handleSend}
                data-loading={loading}
                additionalstyle={`
              width: 72px;
            `}
              >
                {loading ? null : "Send"}
              </Button>
            </div>
          </Footer>
        </>
      )}
      <ModalGeneralSuccessfully
        isOpen={paymentSuccessfullyIsOpen}
        setIsOpen={setPaymentSuccessfullyIsOpen}
        type="checkInPayment"
        callback={setStatus("CHECKED_IN")}
      />
      <ModalGeneralFailed
        isOpen={paymentFailedIsOpen}
        setIsOpen={setPaymentFailedIsOpen}
        type="checkInPayment"
        errorType={errorMessage}
        retryFunction={handleSend}
      />
      {/* <ModalPartialAuthorizationAccepted
        isOpen={partialAuthorizationIsOpen}
        setIsOpen={setPartialAuthorizationIsOpen}
        amount={credit}
        balance={balance}
        retryFunction={handleSend}
      /> */}
    </Container>
  );
};

export default Payment;
